import { LoadingOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Popover, Select, Space, Typography } from 'antd';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { AuthContext } from '../contexts/authContext';

/*
Keeping this from the TSX version so we know which props are passed
interface ISidebarFooterProps {
  showCarriersNavigation?: boolean;
  selectedCarrier?: Carrier;
  carriers?: Carrier[];
}
*/

const SidebarFooter = ({
  carriers,
  collapsed,
  loading,
  selectedCarrier,
  showCarriersNavigation,
}) => {
  const history = useHistory();
  const { authUser, signOut } = useContext(AuthContext);
  const [carrierPopoverOpen, setCarrierPopoverOpen] = useState(false);
  const [userPopoverOpen, setUserPopoverOpen] = useState(false);

  const username = useMemo(() => {
    if (!authUser) {
      return '';
    }
    return `${authUser.firstName || ''} ${authUser.lastName || ''}`;
  }, [authUser]);

  const handleCarrierChange = useCallback(
    (value) => {
      if (value === 'all') {
        history.push('/');
      } else {
        history.push(`/carrier/${value}/`);
      }
    },
    [history]
  );

  const carrierOptions = useMemo(() => {
    const options = [{ label: 'Show All', value: 'all' }];
    if (carriers) {
      carriers.forEach((carrier) => {
        options.push({ label: carrier.name, value: carrier.id.toString() });
      });
    }
    return options;
  }, [carriers]);

  const carrierSelectContent = (
    <Select
      showSearch
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      options={carrierOptions}
      placeholder={loading ? 'Loading Carrier...' : 'Select a carrier'}
      popupMatchSelectWidth={false}
      style={{ width: 200 }}
      suffixIcon={loading ? <LoadingOutlined spin /> : undefined}
      value={loading ? undefined : (selectedCarrier ? selectedCarrier.id.toString() : undefined)}
      onChange={(value) => {
        handleCarrierChange(value);
        setCarrierPopoverOpen(false);
      }}
    />
  );

  const userPopoverContent = (
    <Space direction="vertical" size="small" style={{ minWidth: 150 }}>
      <Typography.Text strong>{username}</Typography.Text>
      <Typography.Text type="secondary">{authUser?.role}</Typography.Text>
      <Button block onClick={signOut}>
        Log out
      </Button>
    </Space>
  );

  if (collapsed) {
    return (
      <div className="sidebar-footer-collapsed">
        {showCarriersNavigation && (
          <Popover
            content={carrierSelectContent}
            open={carrierPopoverOpen}
            placement="right"
            trigger="click"
            onOpenChange={setCarrierPopoverOpen}
          >
            <Button
              icon={loading ? <LoadingOutlined spin /> : <SearchOutlined />}
              shape="circle"
              size="medium"
              style={{ backgroundColor: 'transparent', height: 40, width: 40, }}
              title={selectedCarrier ? selectedCarrier.name : 'Select Carrier'}
            />
          </Popover>
        )}
        <Popover
          content={userPopoverContent}
          open={userPopoverOpen}
          placement="right"
          trigger="click"
          onOpenChange={setUserPopoverOpen}
        >
          <Avatar
            icon={<UserOutlined />}
            size={40}
            style={{ backgroundColor: '#f56a00', cursor: 'pointer' }}
          />
        </Popover>
      </div>
    );
  }

  return (
    <>
      {showCarriersNavigation ? (
        <div className="carriers-navigation">
          <Select
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={carrierOptions}
            placeholder={loading ? 'Loading Carrier...' : 'Select a carrier'}
            popupMatchSelectWidth={false}
            style={{ width: '100%' }}
            suffixIcon={loading ? <LoadingOutlined spin /> : undefined}
            value={
              loading
                ? undefined
                : selectedCarrier
                  ? selectedCarrier.id.toString()
                  : undefined
            }
            onChange={handleCarrierChange}
          />
        </div>
      ) : null}

      <div className="user-profile-section">
        <Avatar
          icon={<UserOutlined />}
          size={54}
          style={{ backgroundColor: '#f56a00', verticalAlign: 'middle' }}
        />
        <div className="user-profile-section__user-info">
          <Typography.Title level={5}>{username}</Typography.Title>
          <Typography.Text>
            <span>{authUser?.role}</span>
            <Button type="link" onClick={signOut}>
              Log out
            </Button>
          </Typography.Text>
        </div>
      </div>

      <div className="version-section">
        <Typography.Text type="secondary">v1.5.0</Typography.Text>
        <Typography.Text type="secondary">(2026-02-05 15:34)</Typography.Text>
      </div>
    </>
  );
};

export default SidebarFooter;
